import { useRef, useEffect, FC, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-final-form';
import { MultiSelectField } from 'components/Forms/MultiSelectField';
import { DragNDropRecExpanded } from 'components/DragNDrop/DragNDropRecExpanded';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsData } from 'selectors/recipients';
import { CustomOption } from './CustomOption';
import { RadioField } from 'components/Forms/RadioField';
import { useFieldValue } from 'hooks/useFormHelpers';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';
import { useFieldRestrictions } from 'hooks/useFieldRestrictions';
import { IPage } from 'store/pages/types';
import { ConfirmationModal } from 'components/Modal/confirmationModal';

type Props = {
  isPreviewVisible: boolean;
  editPageSelected?: IPage;
};

export const RecipientDetailedForm: FC<Props> = ({ isPreviewVisible, editPageSelected }) => {
  const { change } = useForm();
  const recipients = useAppSelector(getRecipientsData);
  const fieldRestrictions = useFieldRestrictions(!!editPageSelected);
  const [isSearchableModalOpen, setIsSearchableModalOpen] = useState(false);

  const recipientsSelected = useFieldValue('recipients_selected');
  const recipientDetails = useFieldValue('recipient_details');
  const processingRedirect = useFieldValue('processing_redirect');

  const paymentProcessorOptions = [
    { value: 'DE_OATH', label: 'Democracy Engine' },
    { value: 'STRIPE', label: 'Stripe' },
  ];

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const recipientOptions =
    recipients
      ?.filter(rec =>
        processingRedirect === 'DE_OATH'
          ? rec.de_status === 'ACTIVE'
          : rec.stripe_status === 'ACTIVE'
      )
      ?.map(rec => ({
        value: rec.recipient_id,
        label: rec.recipient_name || '',
      })) || [];

  const setRecipientDetails = (newRows: any) => {
    change('recipients_selected', newRows);
  };

  const deleteRow = (recipientID: string) => {
    const updatedRecipients = recipientsSelected.filter(row => row.value !== recipientID);
    const updatedRecipientDetails = { ...recipientDetails };
    delete updatedRecipientDetails[recipientID];
    change('recipient_details', updatedRecipientDetails);
    change('recipients_selected', updatedRecipients);
  };

  const addRecipients = (newRecipients: any) => {
    change(
      'recipient_details',
      newRecipients.reduce(
        (acc, rec) => ({
          ...acc,
          [rec.value]: recipientDetails[rec.value] || {
            header_1: '',
            header_2: '',
            summary: '',
            sections: [],
          },
        }),
        { ...recipientDetails }
      )
    );
    change('recipients_selected', newRecipients);
  };

  const handlePublicSearchableChange = (value: boolean) => {
    if (value) {
      setIsSearchableModalOpen(true);
    }
  };

  return (
    <div className="form-group">
      <ConfirmationModal
        isOpen={isSearchableModalOpen}
        handleSave={() => {
          setIsSearchableModalOpen(false);
          change('is_public_searchable', true);
        }}
        handleCancel={() => {
          setIsSearchableModalOpen(false);
          change('is_public_searchable', false);
        }}
        warningMessage="Are you sure you want this page to appear as an Issue for all Oath donors in their profile?"
      />
      <label className="oath-bodysmall">Add and edit your recipient options below.</label>
      <div className="row">
        {fieldRestrictions.canSee('processing_redirect') && (
          <div
            className={classNames('padding0', {
              'col-md-4': !isPreviewVisible,
              'col-md-6': isPreviewVisible,
            })}
          >
            <RadioField
              name="processing_redirect"
              label="Search Recipients by:*"
              options={paymentProcessorOptions}
              disabled={fieldRestrictions.isDisabled('processing_redirect')}
            />
          </div>
        )}
        {fieldRestrictions.canSee('is_public_searchable') && (
          <>
            {!isPreviewVisible && <div className="col-md-2 padding0" />}
            <div className="col-md-6 padding0">
              <div className="margin0 form-group">
                <SwitchToggleField
                  name="is_public_searchable"
                  label="Public Searchable"
                  disabled={fieldRestrictions.isDisabled('is_public_searchable')}
                  onChange={handlePublicSearchableChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div
        className={classNames('padding0', {
          'col-md-12': isPreviewVisible,
        })}
      >
        <MultiSelectField
          name="recipients_selected"
          label="Recipients *"
          placeholder="Search"
          value={recipientsSelected}
          handleChange={addRecipients}
          options={recipientOptions}
          customOption={CustomOption}
        />
        <div className="oath-utility gray">
          Missing a recipient you&apos;re looking for? Please complete your page creation and email
          partners@oath.vote.
        </div>
        <div className="oath-utility gray">Additional recipients can be added later.</div>
        {recipientsSelected?.length > 0 && (
          <>
            <br />
            <div className="oath-utility gray">
              Manually arrange recipients by dragging recipient cards with handle on the right
            </div>
            <DragNDropRecExpanded
              rows={recipientsSelected}
              setRows={setRecipientDetails}
              deleteRow={deleteRow}
            />
          </>
        )}
      </div>
    </div>
  );
};
